import { Popover } from '@material-ui/core';
import { Editor } from '@tiptap/core';
import { Button, Input } from 'copilot-design-system';
import { Formik } from 'formik';
import { useState } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { URLValidationRegex } from 'src/constants';
import { UrlUtils } from 'src/utils';

const linkSchema = z
  .object({
    linkUrl: z.string().regex(URLValidationRegex, 'Please enter a valid URL'),
  })
  .partial();

const LinkForm = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) => {
  const previousUrl: string | undefined = editor.getAttributes('link').href;

  return (
    <Formik
      initialValues={{ linkUrl: previousUrl ?? '' }}
      onSubmit={({ linkUrl }, { resetForm }) => {
        if (linkUrl === '') {
          // unset link
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
          // set link
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: UrlUtils.GetFullUrl(linkUrl) })
            .run();
        }
        resetForm();
        onClose();
      }}
      validationSchema={toFormikValidationSchema(linkSchema)}
      validateOnBlur
      validateOnChange
    >
      {({ errors, handleBlur, handleChange, submitForm, touched, values }) => {
        return (
          <div className="flex flex-col gap-4">
            <Input
              autoFocus
              placeholder="Link"
              name="linkUrl"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.linkUrl}
              error={
                touched.linkUrl && errors.linkUrl !== undefined
                  ? errors.linkUrl
                  : undefined
              }
            />
            <div className="flex flex-row-reverse gap-4 w-">
              <Button label="Add" onClick={submitForm} />
              <Button label="Cancel" onClick={onClose} variant="secondary" />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export const LinkPopover = ({ editor }: { editor: Editor }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        label="Link"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        prefixIcon="Link"
        variant="secondary"
        className="h-10 rounded-none [&>p]:text-[#90959d]"
      />
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: -3,
          horizontal: 'right',
        }}
        onClose={handleClose}
        classes={{
          paper:
            'border border-solid border-secondary-hover shadow-dropdown-menu p-3 w-64',
        }}
      >
        <LinkForm editor={editor} onClose={handleClose} />
      </Popover>
    </>
  );
};
